/**
 * Copyright Schrodinger, LLC
 * All rights reserved.
 *
 * This source code is licensed under the BSD-style license found in the
 * LICENSE file in the root directory of this source tree. An additional grant
 * of patent rights can be found in the PATENTS file in the same directory.
 *
 * @providesModule EventListener
 * @typechecks
 */

import emptyFunction from '../core/emptyFunction';

/**
 * Upstream version of event listener. Does not take into account specific
 * nature of platform.
 */
const EventListener = {
  /**
   * Listen to DOM events during the bubble phase.
   *
   * @param {DOMEventTarget} target DOM element to register listener on.
   * @param {string} eventType Event type, e.g. 'click' or 'mouseover'.
   * @param {function} callback Callback function.
   * @param {object} options Extra options to customize the listener
   * @return {object} Object with a `remove` method.
   */
  listen(target, eventType, callback, options = {}) {
    if (target.addEventListener) {
      target.addEventListener(eventType, callback, options || false);
      return {
        remove() {
          target.removeEventListener(eventType, callback, options || false);
        },
      };
    } else if (target.attachEvent) {
      target.attachEvent('on' + eventType, callback);
      return {
        remove() {
          target.detachEvent('on' + eventType, callback);
        },
      };
    }
  },

  /**
   * Listen to DOM events during the capture phase.
   *
   * @param {DOMEventTarget} target DOM element to register listener on.
   * @param {string} eventType Event type, e.g. 'click' or 'mouseover'.
   * @param {function} callback Callback function.
   * @return {object} Object with a `remove` method.
   */
  capture(target, eventType, callback) {
    if (target.addEventListener) {
      target.addEventListener(eventType, callback, true);
      return {
        remove() {
          target.removeEventListener(eventType, callback, true);
        },
      };
    } else {
      if (__DEV__) {
        console.error(
          'Attempted to listen to events during the capture phase on a ' +
            'browser that does not support the capture phase. Your application ' +
            'will not receive some events.'
        );
      }
      return {
        remove: emptyFunction,
      };
    }
  },

  registerDefault() {},
};

export default EventListener;
